// Must not import ag-grid. Port members stay in method syntax: property syntax makes GridApi
// unassignable to GridStatePort under strictFunctionTypes (ADR 0006, spike b).
import type { Component } from "vue";

import type { ColFilterGroup } from "../columnFilter/filterModel";
import type { ColumnDataType, ColumnFilterConfig } from "../columnFilter/types";
import type { IconName } from "../icons/iconNames.generated";
import type { ColumnAlignment } from "../table/columnAlignment";

export type GridRowId = string;
export type GridAlign = ColumnAlignment;
export type GridEditMode = "none" | "row" | "cell";
/** `definitions`: the columns were set again (a new `columns` prop), not changed by a call or the user. */
export type GridEventSource = "user" | "api" | "autosize" | "fit" | "definitions";
export interface GridCellRef {
  rowId: GridRowId;
  columnId: string;
}

export const GRID_TREE_COLUMN_ID = "ag-Grid-AutoColumn";
export const GRID_SELECTION_COLUMN_ID = "ag-Grid-SelectionColumn";
/** On a cell's root: its content fits itself to the cell's width (a tag list), so a group row widens it. */
export const GRID_CELL_FILLS_WIDTH_CLASS = "sca-cell-fills-width";

/** Columns the grid adds itself (tree, selection). They are never persisted. */
export function isSystemColumnId(columnId: string | null | undefined): boolean {
  return columnId === GRID_TREE_COLUMN_ID || columnId === GRID_SELECTION_COLUMN_ID;
}

export interface GridCellProps<TRow, TValue = unknown, TOptions = Record<string, unknown>> {
  row: TRow;
  rowId: GridRowId;
  value: TValue;
  /** `column.format(value)`: what view mode shows. */
  formatted: string;
  columnId: string;
  align: GridAlign;
  /** `renderer.props` / `editor.props`, resolved for this row. */
  options: TOptions;
  /** The row is in edit mode; read-only cells use it to mirror the edit chrome. */
  editing: boolean;
  getCellElement(): HTMLElement | null;
  tree?: { level: number; expanded: boolean; hasChildren: boolean; setExpanded(expanded: boolean): void };
  /** Set where the Group column shows a group's value; `row` is then only one row of the group. */
  groupRow?: boolean;
}
export interface GridCellEditorProps<TRow, TValue = unknown, TOptions = Record<string, unknown>>
  extends GridCellProps<TRow, TValue, TOptions> {
  startedHere: boolean;
}
export interface GridCellEditorEmits<TRow, TValue = unknown> {
  "update:value": [value: TValue];
  /** Side fields written on commit, e.g. a picked record's name and avatar. */
  "patch-row": [patch: Partial<TRow>];
}
export interface GridCellContext {
  /** The cell shows a group row's value (the Group column); `row` is then only one row of the group. */
  groupRow: boolean;
}
export interface GridCellComponent<TRow = unknown, TOptions = Record<string, unknown>> {
  component: Component;
  props?: TOptions | ((row: TRow, cell: GridCellContext) => TOptions);
}

export interface GridColumn<TRow, TValue = unknown> {
  id: string;
  /** Default: `id`, unless `value` is given. */
  field?: string;
  header: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  hidden?: boolean;
  pinned?: "left" | "right";
  /** `false` keeps the width when the grid fits its columns. */
  fitWidth?: boolean;
  align?: GridAlign;
  sortable?: boolean;
  resizable?: boolean;
  movable?: boolean;
  menu?: boolean;
  /** Floating-filter config; unset or `false` = no floating filter. */
  filter?: ColumnFilterConfig | false;
  /** The filter row keeps this column's model under this id instead of its own. */
  filterColumnId?: string;
  /**
   * How the advanced filter builder and the total row treat the column. Default: derived from
   * `filter`, and none without one, which leaves the column out of the builder.
   */
  filterDataType?: ColumnDataType;
  /** `false` keeps the column out of "Group by" while `rowGroups` is enabled. */
  groupable?: boolean;
  value?: (row: TRow) => TValue;
  // The four callbacks taking TValue use method syntax, so a GridColumn<TRow, number> fits a GridColumn<TRow>[].
  format?(value: TValue, row: TRow): string;
  /** Applied on commit, only when the editor changed the value. */
  parse?(input: unknown, previous: TValue, row: TRow): TValue;
  /** Cell mode: persists the value; returning `false` rejects it. */
  setValue?(row: TRow, value: TValue): boolean;
  /** Client sorting only. */
  compare?(a: TValue, b: TValue, rowA: TRow, rowB: TRow): number;
  renderer?: GridCellComponent<TRow>;
  /** `false` = read-only, whatever `editable` says. */
  editor?: GridCellComponent<TRow> | false;
  /** Default: the column has an editor. Never applies to pinned rows. */
  editable?: boolean | ((row: TRow) => boolean);
  /**
   * Tab stop in row mode. Default: the column has an editor. `true` without an editor makes the
   * control its renderer draws (a radio, a checkbox) a Tab stop of the editing row, skipped in a row
   * whose cell draws none or only a disabled one.
   */
  navigable?: boolean;
  /** The editor offers a clear ×; its slot at the cell end is reserved in view mode too, so nothing shifts. */
  clearable?: boolean;
  /** Adds the generic `grid-cell-invalid` frame. While the row edits, `row` carries the editors' current values. */
  invalid?: (row: TRow) => boolean;
  /** Wraps text and grows the row to fit it. */
  wrap?: boolean;
  /** Grows the row to fit the cell without wrapping its text; `wrap` implies it. */
  autoHeight?: boolean;
  /** The row's cell in this column spans every displayed column to its right. Never applies to pinned rows. */
  spanToEnd?: (row: TRow) => boolean;
  /** Shift+Enter inserts a line break in the editor. */
  multilineEdit?: boolean;
  /** The column's own classes only; never library classes. */
  cellClass?: string | string[] | ((row: TRow) => string | string[] | undefined);
  /** `false` excludes the column from the total row. `icon` is drawn next to the sum, `iconSize` as a CSS size. */
  total?: { format?: (sum: unknown) => string; forced?: boolean; icon?: IconName; iconSize?: string } | false;
  /** Migration escape hatch, counted by the ESLint ratchet. */
  agGrid?: Record<string, unknown>;
}

export type GridSortDirection = "asc" | "desc" | null;
export interface GridColumnState {
  colId: string;
  width?: number;
  hide?: boolean | null;
  pinned?: "left" | "right" | boolean | null;
  sort?: GridSortDirection;
  sortIndex?: number | null;
  flex?: number | null;
  rowGroup?: boolean | null;
}
export interface GridColumnStateUpdate {
  state?: GridColumnState[];
  applyOrder?: boolean;
  defaultState?: Omit<GridColumnState, "colId">;
}
/** The slice of a column definition the shared column editor and the filter row read. */
export interface GridColumnDef {
  headerName?: string;
  cellDataType?: boolean | string;
  autoHeight?: boolean;
  context?: unknown;
  floatingFilterComponentParams?: { columnDataType?: string; originalHiddenColId?: string };
}
export interface GridColumnRef {
  getColId(): string;
  getColDef(): GridColumnDef;
}
export type GridFilterModel = Record<string, ColFilterGroup[]>;
/** Today's persisted JSON shape; a supertype of ag-Grid's model, so it must list all 8 condition kinds. */
export type GridAdvancedFilterModel =
  | { filterType: "join"; type: "AND" | "OR"; conditions: GridAdvancedFilterModel[] }
  | {
      filterType: "text" | "number" | "boolean" | "date" | "dateString" | "dateTime" | "dateTimeString" | "object";
      colId: string;
      type: string;
      filter?: string | number;
    };

/** Grouping of rows by column values ("Row groups"). */
export interface GridRowGroups<TRow = unknown> {
  /** Columns offer "Group by" and the group panel can be shown. */
  enabled: boolean;
  /** Column ids in nesting order: the first one is the top level. */
  columns: string[];
  /**
   * The column the Group column shows in a row's cell, in place of that column. Unset, or hidden by
   * the user, the Group column holds only the groups.
   */
  leafColumn?: string;
  /**
   * A row's group key from its value in a grouped column; null puts the row into the empty group.
   * Unset, a string, number or boolean is its own key and an array is keyed by its entries.
   * Must be the same function on every render, or the grid regroups and collapses every group.
   */
  groupKey?: (value: unknown) => string | null;
  /** Hides the group panel; the grouping stays. */
  panelHidden?: boolean;
  /** Where the group panel is shown; above the header when unset. */
  panelTarget?: HTMLElement | null;
  /**
   * A second button for the panel's menu, e.g. in a toolbar's overflow menu while the panel has no
   * room in the toolbar; its menu lists every grouping column above the actions.
   */
  menuTarget?: GridRowGroupMenuTarget | null;
  /**
   * Groups and their rows come from here while at least one column is grouped by; unset, the grid
   * groups the rows it already has. A new object means the loaded groups are stale.
   */
  source?: GridRowGroupSource<TRow>;
}

export interface GridRowGroupMenuTarget {
  element: HTMLElement;
  /** Closes the menu the button sits in once one of the panel's actions ran. */
  close: () => void;
}

/** One expanded group on the way down: the grouped column and the group's key (null: no value). */
export interface GridRowGroupPathItem {
  columnId: string;
  key: string | null;
}

export interface GridRowGroup<TRow> {
  /** null: the group of rows without a value; the grid shows it as "Blank". */
  key: string | null;
  count: number;
  /** Any row of the group; the Group column renders the group's value from it. */
  row: TRow;
}

/**
 * Loads one level of server-side row groups. A rejected promise marks the block as failed; the
 * source reports the error to the user itself.
 */
export interface GridRowGroupSource<TRow> {
  /**
   * The groups of `columnId` under `path`, in display order. `total` is how many groups the grid
   * shows on this level: for a paged top level, the groups of the current page only.
   */
  loadGroups(request: {
    path: GridRowGroupPathItem[];
    columnId: string;
    offset: number;
    limit: number;
  }): Promise<{ groups: GridRowGroup<TRow>[]; total: number }>;
  /** The rows of the innermost group at the end of `path`. */
  loadRows(request: { path: GridRowGroupPathItem[]; offset: number; limit: number }): Promise<{ rows: TRow[] }>;
}

/** What the shared filterset / column-state composables need. ag-Grid's GridApi satisfies it as-is. */
export interface GridStatePort {
  isDestroyed(): boolean;
  getColumnState(): GridColumnState[];
  applyColumnState(update: GridColumnStateUpdate): boolean;
  resetColumnState(): void;
  setColumnsVisible(columnIds: string[], visible: boolean): void;
  getAllGridColumns(): GridColumnRef[];
  getColumn(columnId: string): GridColumnRef | null;
  getFilterModel(): GridFilterModel;
  setFilterModel(model: GridFilterModel | null): void;
  getAdvancedFilterModel(): GridAdvancedFilterModel | null;
  setAdvancedFilterModel(model: GridAdvancedFilterModel | null): void;
  getColumnFilterInstance(
    columnId: string,
  ): Promise<{ setModel(model: unknown): unknown } | null | undefined> | undefined;
  onFilterChanged(): void;
  refreshCells(params?: { columns?: string[]; force?: boolean }): void;
  getDisplayedRowCount(): number;
  resetRowHeights(): void;
  /** The rows currently in the DOM; `rowHeight` is what the grid last measured or assigned. */
  getRenderedNodes(): { id?: string; rowHeight?: number | null }[];
  getGridOption(key: "defaultColDef"): { width?: number } | undefined;
  getGridOption(key: "autoGroupColumnDef"): { field?: string } | undefined;
  setGridOption(key: "rowHeight", value: number | undefined): void;
  /** Moves the columns without the slide, for a reorder the user did not make. */
  setGridOption(key: "suppressColumnMoveAnimation", value: boolean): void;
  addEventListener(type: GridPortEventType, listener: (event: GridPortEvent) => void): void;
  removeEventListener(type: GridPortEventType, listener: (event: GridPortEvent) => void): void;
}
/** What every ag-Grid event has in common (`type`) next to the neutral `source`; `modelUpdated` carries no `source`. */
export interface GridPortEvent {
  type?: string;
  source?: string;
}
export type GridPortEventType = "newColumnsLoaded" | "columnPinned" | "sortChanged" | "modelUpdated";

export interface GridHandle<TRow = unknown> extends GridStatePort {
  getSelectedRows(): TRow[];
  deselectAll(): void;
  getRow(rowId: GridRowId): TRow | undefined;
  /**
   * The row as its open editors would write it on commit: a copy with their values laid over it, or
   * the row itself while no editor holds one. Reading it neither commits nor closes the edit.
   * `undefined` when the row is not loaded.
   */
  getEditingRow(rowId: GridRowId): TRow | undefined;
  forEachRow(callback: (row: TRow, rowId: GridRowId) => void): void;
  getDisplayedRowIndex(rowId: GridRowId): number | null;
  getDisplayedRowAtIndex(index: number): { rowId: GridRowId; row: TRow } | undefined;
  ensureRowVisible(rowId: GridRowId, position?: "top" | "bottom" | "middle"): void;
  refreshCells(params?: { rowIds?: GridRowId[]; columns?: string[]; force?: boolean }): void;
  redrawRows(params?: { rowIds?: GridRowId[] }): void;
  refreshHeader(): void;
  sizeColumnsToFit(): void;
  getAllDisplayedColumns(): GridColumnRef[];

  expandAll(): void;
  collapseAll(): void;
  isRowExpanded(rowId: GridRowId): boolean;
  setRowExpanded(rowId: GridRowId, expanded: boolean): void;
  /** True for a row with rows below it, which can be opened; false for a leaf or an unknown id. */
  hasChildren(rowId: GridRowId): boolean;
  /**
   * Loads the groups and the rows of every open group again from `rowGroups.source`, keeping the
   * groups open and the scroll where it is. No-op unless the rows come from `rowGroups.source`.
   */
  refreshRowGroups(): void;

  /**
   * Synchronous: the rows are in the grid, and `getRow` and `forEachRow` see them, when it returns.
   * `update` rows are matched by id, `remove` ids the grid does not know are skipped. A row removed
   * and added again in one call comes back collapsed.
   */
  applyTransaction(transaction: { add?: TRow[]; update?: TRow[]; remove?: GridRowId[] }): void;
  /**
   * Replaces one row's data and redraws its cells. Unlike `applyTransaction`, the tree, the open rows
   * and `rowDataUpdated` are left alone. No-op for an id the grid does not know.
   */
  updateRow(rowId: GridRowId, row: TRow): void;
  /** The first and last row in view, in displayed order; null while no row is displayed. */
  getDisplayedRowRange(): { first: number; last: number } | null;
  /** Pixels scrolled from the top of the body; 0 before the grid has a body. */
  getBodyScrollTop(): number;
  setBodyScrollTop(top: number): void;
  /** Drops the highlight kept on the last clicked row. */
  clearActiveRow(): void;
  showNoRowsOverlay(): void;
  /** Sets the tree column's def again, a copy of the one the grid holds; no-op while the grid has none. */
  resetTreeColumn(): void;

  /**
   * Row mode: without `columnId`, starts on the first navigable column. `quiet`: no editor acts as
   * the cell the edit started in, so none focuses itself or opens its list.
   */
  startEditing(rowId: GridRowId, columnId?: string, options?: { quiet?: boolean }): void;
  stopEditing(cancel?: boolean): void;
  getEditingCells(): GridCellRef[];
  /** Editor columns only, unless `controls` adds the renderer controls of `GridColumn.navigable`. */
  getNavigableColumnIds(rowId: GridRowId, options?: { controls?: boolean }): string[];
  /** Grid focus, not DOM focus. */
  setFocusedCell(rowId: GridRowId, columnId: string): void;
  focusEditor(rowId: GridRowId, columnId: string, options?: { open?: boolean; onlyIfLost?: boolean }): void;

  cellAt(target: EventTarget | null): GridCellRef | null;
  /** Skips pinned rows. */
  rowAt(target: EventTarget | null): { rowId: GridRowId; element: HTMLElement } | null;
  /** One element per pinned section, left to right. */
  getRowElements(rowId: GridRowId): HTMLElement[];
  getCellElement(rowId: GridRowId, columnId: string): HTMLElement | null;
  /** The grid's DOM, or a popup or scrollbar the grid owns. */
  isInsideGrid(target: EventTarget | null): boolean;
  /** A popup, menu or scrollbar the grid owns: part of the grid, but of no row or header. */
  isGridOverlay(target: EventTarget | null): boolean;
  /** The expand / collapse arrow of a tree row, so a drag or a click that starts on it can leave it alone. */
  isTreeToggle(target: EventTarget | null): boolean;

  addEventListener<E extends GridEventType>(type: E, listener: (event: GridEvents<TRow>[E]) => void): void;
  removeEventListener<E extends GridEventType>(type: E, listener: (event: GridEvents<TRow>[E]) => void): void;
}

/** Every payload carries `source`, or GridHandle stops satisfying the port it extends. */
export interface GridEventBase {
  source?: GridEventSource;
}
export interface GridEvents<TRow> {
  sortChanged: GridEventBase & { sort: { columnId: string; direction: "asc" | "desc" }[] };
  newColumnsLoaded: GridEventBase;
  columnPinned: GridEventBase & { columnIds: string[]; pinned: "left" | "right" | null };
  columnMoved: GridEventBase & { columnId: string; toIndex: number; finished: boolean };
  columnResized: GridEventBase & { columnIds: string[]; finished: boolean };
  columnVisible: GridEventBase & { columnIds: string[]; visible: boolean };
  modelUpdated: GridEventBase;
  rowDataUpdated: GridEventBase;
  /** A row opened or closed, by the user or through the handle; `GridTreeOptions.onExpandedChange` is user-only. */
  rowGroupOpened: GridEventBase & { rowId: GridRowId; row: TRow; expanded: boolean };
  bodyScroll: GridEventBase & { direction: "horizontal" | "vertical" };
  selectionChanged: GridEventBase & { rows: TRow[] };
  rowEditingStarted: GridEventBase & { rowId: GridRowId; row: TRow; columnId?: string };
  rowEditingStopped: GridEventBase & { rowId: GridRowId; row: TRow };
  /** An open editor of the row changed its value; at most once per row and microtask. Read it with `getEditingRow`. */
  rowEditingDraftChanged: GridEventBase & { rowId: GridRowId };
  cellValueChanged: GridEventBase & {
    rowId: GridRowId;
    row: TRow;
    columnId: string;
    oldValue: unknown;
    newValue: unknown;
  };
}
export type GridEventType = keyof GridEvents<unknown>;

export interface GridSelection<TRow> {
  mode: "single" | "multi";
  selectable?: (row: TRow) => boolean;
  checkbox?: boolean | ((row: TRow) => boolean);
}
export interface GridRowEvent<TRow> {
  row: TRow;
  rowId: GridRowId;
}
export interface GridRowClickEvent<TRow> extends GridRowEvent<TRow> {
  event: Event | null;
}
export interface GridCellClickEvent<TRow> extends GridRowClickEvent<TRow> {
  columnId: string;
}

/** A tree column: rows nest by `path`, and one of the `columns` is drawn as the tree column. */
export interface GridTreeOptions<TRow> {
  path(row: TRow): string[];
  /** Id of the column drawn as the tree column; it is hidden from the normal columns. */
  groupColumnId: string;
  /** Replaces `GridTreeCell`; receives `GridCellProps` with `tree` set. */
  cell?: GridCellComponent<TRow>;
  /** Default: the row has children. */
  arrow?: (row: TRow) => boolean;
  /** Default: `hidden`. `disabled` shows the grip dimmed and inert. */
  dragHandle?: (row: TRow) => "hidden" | "enabled" | "disabled";
  /** Clicking anywhere in the tree cell toggles the row. */
  expandOnCellClick?: boolean;
  indentPerLevel?: number;
  onExpandedChange?: (rowId: GridRowId, expanded: boolean) => void;
  /** Whether a row with children starts expanded; asked when the row first appears. Default: collapsed. */
  expandedByDefault?: (row: TRow) => boolean;
}

/** What the adapter hands `GridTreeCell` as `options`, resolved per row. */
export interface GridTreeCellOptions {
  [key: string]: unknown;
  /** Default: the row has children. */
  arrow?: boolean;
  dragHandle?: "hidden" | "enabled" | "disabled";
  expandOnCellClick?: boolean;
  indentPerLevel?: number;
  onExpandedChange?: (rowId: GridRowId, expanded: boolean) => void;
  inset?: boolean;
  inner?: Component;
  innerOptions?: Record<string, unknown>;
}

export interface GridTotalRowState {
  enabled: boolean;
  position: "top" | "bottom";
}

export interface GridTotalRow extends GridTotalRowState {
  /** Sums by column field; shown in the pinned total row while `enabled`. */
  values?: Record<string, unknown>;
  /** `false` disables the "Total row" menu entry: no checked column would produce a value. */
  hasEligibleColumn?: boolean;
  /** Columns that get the Σ badge in their header. */
  includedColumnIds?: readonly string[];
}

export interface GridFullWidthCellProps<TRow, TOptions = Record<string, unknown>> {
  row: TRow;
  rowId: GridRowId;
  options: TOptions;
}
/** A row spanning every column (a separator or group header). `props` stays one object, so a reactive one reaches the live row. */
export interface GridFullWidthRow<TRow> {
  match(row: TRow): boolean;
  component: Component;
  props?: Record<string, unknown>;
  height?: number;
}

export interface GridOverlayProps<TOptions = Record<string, unknown>> {
  options: TOptions;
}
/** A component shown over the rows; `props` reaches it as `options`, and a changed one reaches the live overlay. */
export interface GridOverlay<TOptions = Record<string, unknown>> {
  component: Component;
  props?: TOptions;
  /**
   * The empty body shrinks to the overlay instead of keeping its minimum height, and the overlay sits under
   * the header; the pointer passes through it to the header, except on the overlay's buttons.
   */
  fitContent?: boolean;
}

export interface GridColumnAlignmentEvent {
  columnId: string;
  alignment: GridAlign;
}

export interface SCADataGridProps<TRow> {
  columns: GridColumn<TRow>[];
  rows: TRow[];
  /** Default: `row.id`. */
  rowId?: (row: TRow) => GridRowId;
  selection?: GridSelection<TRow>;
  /** Matched against the row's `id`, `uiId` or `record.id`; unset keeps the last clicked row highlighted. */
  activeRowId?: GridRowId;
  /** `false`: clicking or focusing a row neither highlights it nor emits `row-click`. */
  rowClick?: boolean;
  /** `false`: the row under the pointer is not highlighted. Read once, when the grid starts. */
  rowHover?: boolean;
  filterRow?: boolean;
  /** Shows the filter row but blocks changing or clearing it. */
  filterRowReadOnly?: boolean;
  advancedFilterOpen?: boolean;
  dateFormat?: string;
  tree?: GridTreeOptions<TRow>;
  /**
   * Columns kept in front of the tree or group column, which the grid otherwise puts first. Set (even
   * empty) to keep the columns where they are on every column change, without a visible jump. Whether
   * it is set at all is fixed when the grid starts.
   */
  leadingColumnIds?: readonly string[];
  /** Unset: the grid has no total row and the menu entry stays disabled. */
  totalRow?: GridTotalRow;
  /** Per-column alignment chosen by the user; unset hides the "Column alignment" menu entry. */
  columnAlignment?: Record<string, GridAlign>;
  /** CSS `top` of the sticky header. */
  stickyHeaderTop?: string;
  fullWidthRow?: GridFullWidthRow<TRow>;
  /** Runs after every sort; may return the rows in any order (pinning a separator on top). */
  reorderAfterSort?: (rows: TRow[]) => TRow[];
  /** `server`: rows arrive sorted, and a header click only reports the new sort. */
  sorting?: "client" | "server";
  autoHeight?: boolean;
  /**
   * Any value above 0 turns it on; the number itself is not read. The grid is then at least as high
   * as its displayed rows (two while it has none) at 40px each plus the header, capped at 494px.
   */
  minRowCount?: number;
  compact?: boolean;
  valign?: "top" | "center";
  /** Hides rows while row heights are measured or a filterset switch is in flight. */
  transitioning?: boolean;
  /** Skeleton rows over the body, also over rows still shown (a sort waiting for data). */
  loading?: boolean;
  /** `none`: no cell opens an editor. Read once, when the grid starts, like `editTrigger`. */
  editMode?: GridEditMode;
  editTrigger?: "doubleClick" | "click";
  /** A function is read per row; whether one is given at all is fixed when the grid starts. */
  rowHeight?: number | ((row: TRow) => number | undefined);
  /** Whether one is given at all is fixed when the grid starts. */
  rowClass?: (row: TRow) => string | string[] | undefined;
  /** Fits the columns to the grid width on start, on resize, and after any column is resized, shown or hidden. */
  fitColumns?: boolean;
  columnsMovable?: boolean;
  columnHide?: boolean;
  alwaysVisibleColumnIds?: string[];
  columnReset?: boolean;
  noRowsText?: string;
  noRowsOverlay?: boolean;
  /** Replaces the no-rows text. Whether one is given at all is fixed when the grid starts. */
  noRows?: GridOverlay;
  /** Cell text can be selected with the mouse. Read once, when the grid starts. */
  textSelection?: boolean;
  /** Unset: the grid cannot group rows. */
  rowGroups?: GridRowGroups<TRow>;
  /** Migration escape hatch, counted by the ESLint ratchet; read once, when the grid starts. */
  agGridOptions?: Record<string, unknown>;
}

export interface SCADataGridEmits<TRow> {
  ready: [handle: GridHandle<TRow>];
  "filter-changed": [groups: ColFilterGroup[]];
  "advanced-filter-changed": [model: GridAdvancedFilterModel];
  "column-resized": [event: GridEvents<TRow>["columnResized"]];
  "column-moved": [event: GridEvents<TRow>["columnMoved"]];
  "column-visible": [event: GridEvents<TRow>["columnVisible"]];
  "sort-changed": [event: GridEvents<TRow>["sortChanged"]];
  "selection-changed": [event: GridEvents<TRow>["selectionChanged"]];
  "row-click": [event: GridRowClickEvent<TRow>];
  "row-double-click": [event: GridRowEvent<TRow>];
  "cell-click": [event: GridCellClickEvent<TRow>];
  "cell-value-changed": [event: GridEvents<TRow>["cellValueChanged"]];
  /** In cell mode, for the row that holds the edited cell. */
  "row-edit-started": [event: GridEvents<TRow>["rowEditingStarted"]];
  "row-edit-stopped": [event: GridEvents<TRow>["rowEditingStopped"]];
  "reset-columns": [];
  "manage-columns": [];
  "update:column-alignment": [event: GridColumnAlignmentEvent];
  "update:total-row-state": [state: GridTotalRowState];
  /** The column menu's "Expand all" / "Collapse all" on a tree; the grid has already done it, unless the rows are dossier rows. */
  "expand-all": [];
  "collapse-all": [];
  /** The user changed the grouping columns; ids in nesting order. */
  "update:row-group-columns": [columnIds: string[]];
  "hide-row-group-panel": [];
}
